import React, { useState, useEffect, useCallback } from 'react';
import { CheckCircle, XCircle, Eye, Clock, User, Phone, Calendar, CreditCard, FileText, Truck, Building, Trash2 } from 'lucide-react';
import * as api from '../services/api';
import { useAuth } from '../context/AuthContext';

const STATUS_LABELS = { pending: 'Pendiente', approved: 'Aprobado', rejected: 'Rechazado' };
const STATUS_COLORS = {
  pending:  { bg: 'var(--warning-bg)', text: 'var(--warning-text)' },
  approved: { bg: 'var(--success-bg)', text: 'var(--success-text)' },
  rejected: { bg: 'var(--error-bg)',   text: 'var(--error-text)' },
};
const ZONES = ['Zona Norte', 'Zona Sur', 'Zona Este', 'Zona Oeste', 'Zona Centro'];

const VEHICLE_LABELS = { bicicleta: 'Bicicleta', moto: 'Moto', auto: 'Auto' };

const PHOTO_GROUPS = [
  { key: 'dni',     label: 'DNI',                 front: 'dni_front_url',     back: 'dni_back_url' },
  { key: 'license', label: 'Licencia de conducir', front: 'license_front_url', back: 'license_back_url' },
];

function formatDate(d) {
  return new Date(d).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatDateTime(d) {
  return new Date(d).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

// ─── Detail modal ───────────────────────────────────────────────────────────
function DetailModal({ reg, onClose, onApprove, onReject, isAdminPlus }) {
  const [zone, setZone] = useState('');
  const [role, setRole] = useState('lector');
  const [rejectReason, setRejectReason] = useState('');
  const [view, setView] = useState('info');
  const [loading, setLoading] = useState(false);
  const [zoomUrl, setZoomUrl] = useState(null);

  async function handleApprove() {
    if (!zone) return alert('Seleccioná una zona');
    setLoading(true);
    try { await onApprove(reg.id, zone, role); } finally { setLoading(false); }
  }

  async function handleReject() {
    setLoading(true);
    try { await onReject(reg.id, rejectReason); } finally { setLoading(false); }
  }

  const needsVehicleInsurance = reg.vehicle_type === 'moto' || reg.vehicle_type === 'auto';

  return (
    <div style={modal.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      {zoomUrl && (
        <div style={{ ...modal.overlay, zIndex: 200, background: 'rgba(0,0,0,0.92)' }}
          onClick={() => setZoomUrl(null)}>
          <img src={zoomUrl} alt="zoom" style={{ maxWidth: '90vw', maxHeight: '90vh', borderRadius: 12 }} />
          <p style={{ color: '#fff', marginTop: 12, fontSize: 13 }}>Clic para cerrar</p>
        </div>
      )}

      <div style={modal.box}>
        {/* Header */}
        <div style={modal.header}>
          <div>
            <h2 style={modal.title}>{reg.first_name} {reg.last_name}</h2>
            <p style={modal.sub}>{reg.email} · Solicitado el {formatDateTime(reg.created_at)}</p>
          </div>
          <button style={modal.closeBtn} onClick={onClose}>✕</button>
        </div>

        {/* Tabs */}
        <div style={modal.tabs}>
          {[['info','Información'], ...(isAdminPlus ? [['approve','Aprobar'],['reject','Rechazar']] : [])].map(([v,l]) => (
            <button key={v} style={{ ...modal.tab, ...(view === v ? modal.tabActive : {}) }}
              onClick={() => setView(v)}>{l}</button>
          ))}
        </div>

        <div style={modal.body}>
          {/* ── INFO ── */}
          {view === 'info' && (
            <div>

              {/* Datos personales */}
              <Section title="Datos personales">
                <div style={modal.grid2}>
                  <InfoItem icon={<User size={14}/>}       label="Nombre"     value={`${reg.first_name} ${reg.last_name}`} />
                  <InfoItem icon={<CreditCard size={14}/>} label="DNI"        value={reg.dni} />
                  <InfoItem icon={<Calendar size={14}/>}   label="Nacimiento" value={formatDate(reg.birth_date)} />
                  <InfoItem icon={<Phone size={14}/>}      label="Teléfono"   value={reg.phone} />
                </div>
              </Section>

              {/* DNI */}
              <Section title="DNI">
                <PhotoRow>
                  <PhotoThumb url={reg.dni_front_url} label="Frente" onZoom={setZoomUrl} />
                  <PhotoThumb url={reg.dni_back_url}  label="Dorso"  onZoom={setZoomUrl} />
                </PhotoRow>
              </Section>

              {/* Licencia */}
              <Section title="Licencia de conducir">
                <PhotoRow>
                  <PhotoThumb url={reg.license_front_url} label="Frente" onZoom={setZoomUrl} />
                  <PhotoThumb url={reg.license_back_url}  label="Dorso"  onZoom={setZoomUrl} />
                </PhotoRow>
              </Section>

              {/* Cara + selfie */}
              <Section title="Identificación fotográfica">
                <PhotoRow>
                  <PhotoThumb url={reg.face_front_url} label="Foto de cara"  onZoom={setZoomUrl} />
                  <PhotoThumb url={reg.selfie_dni_url} label="Selfie con DNI" onZoom={setZoomUrl} />
                </PhotoRow>
              </Section>

              {/* Vehículo */}
              <Section title="Vehículo de trabajo">
                <div style={{ ...modal.grid2, marginBottom: needsVehicleInsurance ? 16 : 0 }}>
                  <InfoItem icon={<Truck size={14}/>} label="Tipo de vehículo"
                    value={VEHICLE_LABELS[reg.vehicle_type] || reg.vehicle_type || '—'} />
                </div>
                {needsVehicleInsurance && (
                  <>
                    <p style={{ ...modal.sectionTitle, marginBottom: 10 }}>Licencia de conducir</p>
                    <PhotoRow style={{ marginBottom: 16 }}>
                      <PhotoThumb url={reg.license_front_url} label="Frente" onZoom={setZoomUrl} />
                      <PhotoThumb url={reg.license_back_url}  label="Dorso"  onZoom={setZoomUrl} />
                    </PhotoRow>
                    <p style={{ ...modal.sectionTitle, margin: '16px 0 10px' }}>Cédula del vehículo</p>
                    <PhotoRow>
                      <PhotoThumb url={reg.vehicle_cedula_front_url} label="Frente" onZoom={setZoomUrl} />
                      <PhotoThumb url={reg.vehicle_cedula_back_url}  label="Dorso"  onZoom={setZoomUrl} />
                    </PhotoRow>
                    <p style={{ ...modal.sectionTitle, margin: '16px 0 10px' }}>Seguro del vehículo</p>
                    <FileRow>
                      <FileThumb url={reg.vehicle_insurance_url} label={`Seguro de ${reg.vehicle_type}`} onZoom={setZoomUrl} />
                      <div style={{ flex: 1 }} />
                    </FileRow>
                  </>
                )}
              </Section>


              {/* Datos de pago */}
              <Section title="Datos de pago">
                <div style={{ ...modal.grid2, marginBottom: 16 }}>
                  <InfoItem icon={<CreditCard size={14}/>} label="CBU"   value={reg.cbu} />
                  <InfoItem icon={<Building size={14}/>}   label="Banco" value={reg.bank} />
                </div>
                <FileRow>
                  <FileThumb url={reg.monotributo_url} label="Comprobante de monotributo" onZoom={setZoomUrl} />
                  <div style={{ flex: 1 }} />
                </FileRow>
              </Section>

            </div>
          )}

          {/* ── APPROVE ── */}
          {view === 'approve' && (
            <div>
              <p style={{ color:'var(--text-2)', marginBottom:20, fontSize:14 }}>
                Al aprobar, se creará una cuenta de operario y el usuario podrá iniciar sesión.
              </p>
              <label style={form.label}>Rol *</label>
              <div style={{ display:'flex', gap:8, marginBottom:20 }}>
                {[['lector','Auditor'],['lector-plus','Auditor Plus']].map(([val, label]) => (
                  <button key={val}
                    style={{ ...form.zonePill, ...(role === val ? form.zonePillActive : {}) }}
                    onClick={() => setRole(val)}>{label}</button>
                ))}
              </div>
              <label style={form.label}>Zona asignada *</label>
              <div style={{ display:'flex', flexWrap:'wrap', gap:8, marginBottom:24 }}>
                {ZONES.map(z => (
                  <button key={z}
                    style={{ ...form.zonePill, ...(zone === z ? form.zonePillActive : {}) }}
                    onClick={() => setZone(z)}>{z}</button>
                ))}
              </div>
              <button style={{ ...form.btnPrimary, opacity: (!zone || loading) ? 0.6 : 1 }}
                disabled={!zone || loading} onClick={handleApprove}>
                {loading ? 'Aprobando...' : '✓ Aprobar solicitud'}
              </button>
            </div>
          )}

          {/* ── REJECT ── */}
          {view === 'reject' && (
            <div>
              <p style={{ color:'var(--text-2)', marginBottom:16, fontSize:14 }}>
                El operario recibirá el motivo al intentar iniciar sesión.
              </p>
              <label style={form.label}>Motivo del rechazo (opcional)</label>
              <textarea
                style={form.textarea}
                rows={4}
                placeholder="Ej: Documentación ilegible, datos incorrectos..."
                value={rejectReason}
                onChange={e => setRejectReason(e.target.value)}
              />
              <button style={{ ...form.btnDanger, opacity: loading ? 0.6 : 1 }}
                disabled={loading} onClick={handleReject}>
                {loading ? 'Rechazando...' : '✕ Rechazar solicitud'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function Section({ title, children }) {
  return (
    <div style={modal.section}>
      <div style={modal.sectionHeader}>
        <span style={modal.sectionTitle}>{title}</span>
      </div>
      {children}
    </div>
  );
}

function PhotoRow({ children }) {
  return <div style={{ display: 'flex', gap: 12 }}>{children}</div>;
}

function FileRow({ children }) {
  return <div style={{ display: 'flex', gap: 12 }}>{children}</div>;
}

function InfoItem({ icon, label, value }) {
  return (
    <div style={{ background:'var(--bg-subtle)', borderRadius:10, padding:'11px 14px' }}>
      <div style={{ display:'flex', alignItems:'center', gap:6, color:'var(--text-3)', marginBottom:5 }}>
        {icon}
        <span style={{ fontSize: 11, fontWeight: 700, textTransform:'uppercase', letterSpacing:'0.06em' }}>{label}</span>
      </div>
      <p style={{ margin:0, fontSize:14, fontWeight: 600, color:'var(--text)', wordBreak:'break-all' }}>{value || '—'}</p>
    </div>
  );
}

function PhotoThumb({ url, label, onZoom }) {
  const labelEl = <p style={thumb.label}>{label}</p>;
  if (!url) return (
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={thumb.empty}><span style={{ fontSize: 13, color:'var(--text-3)' }}>Sin foto</span></div>
      {labelEl}
    </div>
  );
  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      <img src={url} alt={label}
        style={{ ...thumb.photo, cursor:'zoom-in' }}
        onClick={() => onZoom(url)} />
      {labelEl}
    </div>
  );
}

function FileThumb({ url, label, onZoom }) {
  const labelEl = <p style={thumb.label}>{label}</p>;

  if (!url) return (
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ ...thumb.file, background:'var(--bg-subtle)' }}>
        <FileText size={22} color="var(--text-3)" />
        <span style={{ fontSize: 12, color:'var(--text-3)', fontWeight: 600 }}>Sin archivo</span>
      </div>
      {labelEl}
    </div>
  );

  const isPdf = url.includes('/raw/') || url.toLowerCase().endsWith('.pdf');

  if (isPdf) return (
    <div style={{ flex: 1, minWidth: 0 }}>
      <a href={url} target="_blank" rel="noreferrer" style={{ textDecoration:'none', display:'block' }}>
        <div style={{ ...thumb.file, background:'#FBF0E8', border:'1.5px solid #D4620D22' }}>
          <FileText size={28} color="#D4620D" />
          <span style={{ fontSize: 13, color:'#D4620D', fontWeight: 700 }}>Abrir PDF</span>
        </div>
      </a>
      {labelEl}
    </div>
  );

  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      <img src={url} alt={label}
        style={{ ...thumb.photo, cursor:'zoom-in' }}
        onClick={() => onZoom(url)} />
      {labelEl}
    </div>
  );
}

// ─── Delete confirm modal ────────────────────────────────────────────────────
function DeleteConfirmModal({ onConfirm, onClose }) {
  const [pwd, setPwd] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    if (pwd !== '2528') { setError('Contraseña incorrecta'); setPwd(''); return; }
    setLoading(true);
    try { await onConfirm(); } finally { setLoading(false); }
  }

  return (
    <div style={modal.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background:'var(--bg-card)', borderRadius:20, width:'100%', maxWidth:400, padding:32, boxShadow:'0 20px 60px rgba(0,0,0,0.2)' }}>
        <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:8 }}>
          <div style={{ width:42, height:42, borderRadius:12, background:'#fef2f2', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
            <Trash2 size={20} color="#dc2626" />
          </div>
          <h3 style={{ margin:0, fontSize:18, fontWeight:700, color:'var(--text)' }}>Eliminar solicitud</h3>
        </div>
        <p style={{ margin:'0 0 24px', fontSize:14, color:'var(--text-2)', lineHeight:1.5 }}>
          Esta acción no se puede deshacer. Ingresá la contraseña para confirmar.
        </p>
        <form onSubmit={handleSubmit}>
          <input
            type="password"
            autoFocus
            placeholder="Contraseña"
            value={pwd}
            onChange={e => { setPwd(e.target.value); setError(''); }}
            style={{ width:'100%', padding:'12px 14px', borderRadius:12, border:`1.5px solid ${error ? '#dc2626' : 'var(--border)'}`, fontSize:14, fontFamily:'inherit', boxSizing:'border-box', outline:'none', marginBottom: error ? 8 : 20 }}
          />
          {error && <p style={{ margin:'0 0 16px', fontSize:13, color:'#dc2626', fontWeight:600 }}>{error}</p>}
          <div style={{ display:'flex', gap:10 }}>
            <button type="button" onClick={onClose}
              style={{ flex:1, padding:13, borderRadius:12, border:'1.5px solid var(--border)', background:'var(--bg-card)', fontSize:14, fontWeight:700, color:'var(--text-2)', cursor:'pointer' }}>
              Cancelar
            </button>
            <button type="submit" disabled={!pwd || loading}
              style={{ flex:1, padding:13, borderRadius:12, border:'none', background: (!pwd || loading) ? '#f87171' : '#dc2626', color:'#fff', fontSize:14, fontWeight:700, cursor: (!pwd || loading) ? 'not-allowed' : 'pointer' }}>
              {loading ? 'Eliminando...' : 'Eliminar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Main page ───────────────────────────────────────────────────────────────
export default function ApprovalsPage() {
  const { user } = useAuth();
  const isAdminPlus = user?.role === 'admin-plus';
  const [registrations, setRegistrations] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [status, setStatus] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.getRegistrations(status);
      setRegistrations(data.data);
      setPendingCount(data.pendingCount);
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  async function handleApprove(id, zone, role) {
    await api.approveRegistration(id, zone, role);
    showToast('Solicitud aprobada correctamente');
    setSelected(null);
    load();
  }

  async function handleReject(id, reason) {
    await api.rejectRegistration(id, reason);
    showToast('Solicitud rechazada');
    setSelected(null);
    load();
  }

  async function handleDelete() {
    try {
      await api.deleteRegistration(deleteTarget);
      setDeleteTarget(null);
      showToast('Solicitud eliminada');
      load();
    } catch (e) {
      showToast(e.message, 'error');
    }
  }

  async function openDetail(item) {
    try {
      const full = await api.getRegistration(item.id);
      setSelected(full);
    } catch (e) {
      showToast(e.message, 'error');
    }
  }

  return (
    <div>
      {/* Toast */}
      {toast && (
        <div style={{ ...page.toast, background: toast.type === 'error' ? '#ef4444' : '#22c55e' }}>
          {toast.msg}
        </div>
      )}

      {selected && (
        <DetailModal
          reg={selected}
          onClose={() => setSelected(null)}
          onApprove={handleApprove}
          onReject={handleReject}
          isAdminPlus={isAdminPlus}
        />
      )}

      {deleteTarget && (
        <DeleteConfirmModal
          onConfirm={handleDelete}
          onClose={() => setDeleteTarget(null)}
        />
      )}

      {/* Header */}
      <div style={page.header}>
        <div>
          <h1 style={page.title}>Solicitudes de registro</h1>
          <p style={page.sub}>Revisá y aprobá los nuevos operarios</p>
        </div>
        {pendingCount > 0 && (
          <div style={page.badge}>
            <Clock size={14} />
            {pendingCount} pendiente{pendingCount !== 1 ? 's' : ''}
          </div>
        )}
      </div>

      {/* Filter tabs */}
      <div style={page.filterRow}>
        {['pending', 'approved', 'rejected'].map(s => (
          <button key={s}
            style={{ ...page.filterBtn, ...(status === s ? page.filterBtnActive : {}) }}
            onClick={() => setStatus(s)}>
            {STATUS_LABELS[s]}
          </button>
        ))}
      </div>

      {/* Table */}
      {loading ? (
        <div style={page.center}><span style={{ color:'var(--text-2)' }}>Cargando...</span></div>
      ) : registrations.length === 0 ? (
        <div style={page.center}>
          <CheckCircle size={40} color="#9a9a93" style={{ marginBottom:12 }} />
          <p style={{ color:'var(--text-2)', margin:0 }}>No hay solicitudes {STATUS_LABELS[status].toLowerCase()}s</p>
        </div>
      ) : (
        <div style={page.card}>
          <table style={page.table}>
            <thead>
              <tr>
                {['Nombre','Email','DNI','Vehículo','Fecha','Estado',''].map(h => (
                  <th key={h} style={page.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {registrations.map(r => (
                <tr key={r.id} style={page.tr}>
                  <td style={page.td}>
                    <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                      <div style={page.avatar}>
                        {r.first_name[0]}{r.last_name[0]}
                      </div>
                      <div>
                        <p style={page.tdName}>{r.first_name} {r.last_name}</p>
                      </div>
                    </div>
                  </td>
                  <td style={page.td}><span style={page.tdSub}>{r.email}</span></td>
                  <td style={page.td}><span style={page.tdSub}>{r.dni}</span></td>
                  <td style={page.td}>
                    <span style={page.tdSub}>{VEHICLE_LABELS[r.vehicle_type] || '—'}</span>
                  </td>
                  <td style={page.td}><span style={page.tdSub}>{formatDate(r.created_at)}</span></td>
                  <td style={page.td}>
                    <span style={{
                      ...page.pill,
                      background: STATUS_COLORS[r.status].bg,
                      color: STATUS_COLORS[r.status].text,
                    }}>
                      {STATUS_LABELS[r.status]}
                    </span>
                  </td>
                  <td style={page.td}>
                    <div style={{ display:'flex', gap:8 }}>
                      <button style={page.viewBtn} onClick={() => openDetail(r)}>
                        <Eye size={14} /> Ver
                      </button>
                      {status === 'rejected' && isAdminPlus && (
                        <button style={page.deleteBtn} onClick={() => setDeleteTarget(r.id)}>
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Styles ──────────────────────────────────────────────────────────────────
const page = {
  header: { display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:24 },
  title: { fontSize:26, fontWeight: 700, color:'var(--text)', margin:'0 0 4px', letterSpacing:'-0.5px' },
  sub: { color:'var(--text-2)', margin:0, fontSize:14 },
  badge: { display:'flex', alignItems:'center', gap:6, background:'var(--warning-bg)', color:'var(--warning-text)', padding:'8px 14px', borderRadius:100, fontSize:13, fontWeight: 700 },
  filterRow: { display:'flex', gap:8, marginBottom:20 },
  filterBtn: { padding:'8px 18px', borderRadius:8, border:'1.5px solid var(--border)', background:'var(--bg-card)', fontSize:13, fontWeight: 700, color:'var(--text-2)', cursor:'pointer' },
  filterBtnActive: { background:'var(--text)', color:'var(--bg)', borderColor:'var(--text)' },
  card: { background:'var(--bg-card)', borderRadius:16, overflow:'hidden', boxShadow:'0 1px 3px rgba(0,0,0,0.06)' },
  table: { width:'100%', borderCollapse:'collapse' },
  th: { padding:'12px 16px', textAlign:'left', fontSize: 13, fontWeight: 700, color:'var(--text-3)', textTransform:'uppercase', letterSpacing:'0.05em', borderBottom:'1px solid var(--border)' },
  tr: { borderBottom:'1px solid var(--border)', transition:'background 0.1s' },
  td: { padding:'14px 16px', verticalAlign:'middle' },
  tdName: { margin:0, fontSize:14, fontWeight: 700, color:'var(--text)' },
  tdSub: { fontSize:13, color:'var(--text-2)' },
  avatar: { width:34, height:34, borderRadius:'50%', background:'var(--bg-subtle)', color:'var(--text-2)', display:'flex', alignItems:'center', justifyContent:'center', fontSize: 13, fontWeight: 700, flexShrink:0 },
  pill: { padding:'3px 10px', borderRadius:100, fontSize: 13, fontWeight: 700, display:'inline-block' },
  viewBtn: { display:'flex', alignItems:'center', gap:5, padding:'7px 12px', borderRadius:8, border:'1.5px solid var(--border)', background:'var(--bg-card)', fontSize:13, fontWeight: 700, color:'var(--text-2)', cursor:'pointer' },
  deleteBtn: { display:'flex', alignItems:'center', justifyContent:'center', padding:'7px 10px', borderRadius:8, border:'1.5px solid #fee2e2', background:'#fef2f2', color:'#dc2626', cursor:'pointer' },
  center: { display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:'80px 0', color:'var(--text-3)' },
  toast: { position:'fixed', top:20, right:20, zIndex:999, padding:'12px 20px', borderRadius:10, color:'#fff', fontSize:13, fontWeight: 700, boxShadow:'0 4px 20px rgba(0,0,0,0.2)' },
};

const modal = {
  overlay: { position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:100, display:'flex', alignItems:'center', justifyContent:'center', padding:24, flexDirection:'column' },
  box: { background:'var(--bg-card)', borderRadius:20, width:'100%', maxWidth:800, maxHeight:'92vh', overflow:'hidden', display:'flex', flexDirection:'column', boxShadow:'0 20px 60px rgba(0,0,0,0.2)' },
  header: { display:'flex', justifyContent:'space-between', alignItems:'flex-start', padding:'22px 28px 0' },
  title: { margin:'0 0 4px', fontSize:20, fontWeight: 700, color:'var(--text)' },
  sub: { margin:0, fontSize:13, color:'var(--text-2)' },
  closeBtn: { background:'none', border:'none', cursor:'pointer', fontSize:20, color:'var(--text-3)', padding:4, lineHeight:1 },
  tabs: { display:'flex', gap:0, padding:'14px 28px 0', borderBottom:'1px solid var(--border)' },
  tab: { padding:'10px 20px', background:'none', border:'none', borderBottom:'2px solid transparent', cursor:'pointer', fontSize:13, fontWeight: 700, color:'var(--text-2)', marginBottom:-1 },
  tabActive: { color:'var(--text)', borderBottom:'2px solid #D4620D' },
  body: { padding:'20px 28px 28px', overflowY:'auto', flex:1 },
  section: { marginBottom:28 },
  sectionHeader: { display:'flex', alignItems:'center', gap:8, marginBottom:12, paddingBottom:8, borderBottom:'1px solid var(--border)' },
  sectionTitle: { fontSize: 11, fontWeight: 700, color:'var(--text-3)', textTransform:'uppercase', letterSpacing:'0.08em', margin:0 },
  grid2: { display:'grid', gridTemplateColumns:'repeat(2, 1fr)', gap:10 },
  grid3: { display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:10 },
};

const thumb = {
  photo: { width:'100%', height:200, objectFit:'cover', borderRadius:12, display:'block' },
  file: { width:'100%', height:140, borderRadius:12, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:10, border:'1px solid var(--border)' },
  empty: { width:'100%', height:200, borderRadius:12, display:'flex', alignItems:'center', justifyContent:'center', background:'var(--bg-subtle)' },
  label: { margin:'7px 0 0', fontSize:11, color:'var(--text-3)', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.06em' },
};

const form = {
  label: { display:'block', fontSize: 13, fontWeight: 700, color:'var(--text-2)', textTransform:'uppercase', letterSpacing:'0.04em', marginBottom:10 },
  zonePill: { padding:'10px 16px', borderRadius:10, border:'1.5px solid var(--border)', background:'var(--bg-card)', fontSize:13, fontWeight: 700, color:'var(--text-2)', cursor:'pointer' },
  zonePillActive: { background:'#D4620D', color:'#fff', borderColor:'#D4620D' },
  textarea: { width:'100%', padding:'12px 14px', borderRadius:12, border:'1px solid var(--border)', fontSize:14, fontFamily:'inherit', resize:'vertical', boxSizing:'border-box', marginBottom:16, outline:'none' },
  btnPrimary: { width:'100%', padding:16, borderRadius:12, border:'none', background:'#16a34a', color:'#fff', fontSize:15, fontWeight: 700, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:8 },
  btnDanger: { width:'100%', padding:16, borderRadius:12, border:'none', background:'#dc2626', color:'#fff', fontSize:15, fontWeight: 700, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:8 },
};
